import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, of } from 'rxjs';
import { ICON } from '@shared/constants/icons.constant';
import { hasUnsupportedScope, SearchConnector, SearchResultItem, SearchScope } from '@shared/global-search';
import { Icon } from '@shared/types/icon.type';
import { DocumentsService, DocumentSearchHit } from '../../../shared/services/entity/documents.service';

const MAX_RESULTS = 6;

/**
 * Searches the DMS documents by title / filename / metadata through the entity list filter.
 */
@Injectable({ providedIn: 'root' })
export class DocumentsSearchConnector implements SearchConnector {
    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly entityService = inject(DocumentsService);

    private readonly router = inject(Router);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    readonly id = 'documents';

    readonly label = 'Dokumente';

    readonly order = 22;

    readonly icon: Icon = ICON.fileText;

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    search(query: string, scope: SearchScope): Observable<SearchResultItem[]> {
        // The DMS search honors every entity scope via the elastic term filters
        if (hasUnsupportedScope(scope, ['propertyId', 'unitId', 'contactId', 'contractorId'])) {
            return of([]);
        }

        const context = scope.context ?? {};

        return this.entityService
            .search(query, MAX_RESULTS, {
                propertyId: context['propertyId'] ? String(context['propertyId']) : undefined,
                unitId: context['unitId'] ? String(context['unitId']) : undefined,
                contactId: context['contactId'] ? String(context['contactId']) : undefined,
                contractorId: context['contractorId'] ? String(context['contractorId']) : undefined,
            })
            .pipe(map((hits) => hits.map((hit) => this.toResultItem(hit))));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE METHODS
    // -----------------------------------------------------------------------------------------------------

    private toResultItem(document: DocumentSearchHit): SearchResultItem {
        return {
            id: document.id,
            title: document.title || document.fileName,
            subtitle: document.type,
            icon: this.icon,
            payload: document,
            actions: [
                {
                    id: 'open',
                    label: 'Öffnen',
                    icon: ICON.caretRight,
                    execute: () => {
                        this.router.navigate(['/documents', document.id]);
                    },
                },
            ],
        };
    }
}